import { AisType, type SiteDetail } from "@plenum/shared/api";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { ApiError, usePatchSite } from "../../lib/api";
import { fmtCoord, fmtDate, fmtInt, fmtKm, fmtMw } from "../../lib/format";
import { googleEarth, googleMapsSatellite, openStreetMap, streetView } from "../../lib/map-styles";
import { AIS_LABEL, label, SECTOR_LABEL, SITE_TYPE_LABEL } from "../../lib/tiers";
import { Pill } from "../Badge";
import { useToast } from "../Toast";
import type { VerifyController } from "./SitePanel";

export function OverviewTab({
  detail,
  verify,
}: {
  detail: SiteDetail;
  verify: VerifyController | null;
}) {
  const s = detail.site;
  const patch = usePatchSite();
  const toast = useToast();
  const [aliasDraft, setAliasDraft] = useState<string | null>(null);
  const pinActive = verify?.pin?.siteId === s.id;
  const inBand = detail.units.filter((u) => u.in_band);
  const perf = inBand.map((u) => u.genset.performance).filter(Boolean);
  const avg = (vals: Array<number | null | undefined>) => {
    const xs = vals.filter((v): v is number => typeof v === "number");
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
  };
  const meanAge = avg(perf.map((p) => p?.age_years));
  const meanEff = avg(perf.map((p) => p?.efficiency_best_pct));

  async function saveAis(value: string) {
    try {
      await patch.mutateAsync({
        siteId: s.id,
        patch: { ais_type: value as SiteDetail["site"]["ais_type"] as never },
      });
      toast("AIS type saved as a reviewer claim");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Save failed", "danger");
    }
  }

  async function saveAliases() {
    if (aliasDraft === null) return;
    const aliases = aliasDraft
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);
    try {
      await patch.mutateAsync({ siteId: s.id, patch: { aliases } });
      setAliasDraft(null);
      toast("Aliases saved");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Save failed", "danger");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="grid grid-cols-3 gap-2">
        <Kpi
          label="In-band units"
          value={fmtInt(inBand.length)}
          sub={`${fmtInt(detail.units.length)} total`}
        />
        <Kpi label="Qualifying MW" value={fmtMw(s.qualifying_mw)} />
        <Kpi
          label="Est. MWh/yr"
          value={detail.totals.est_mwh_yr === null ? "—" : fmtInt(detail.totals.est_mwh_yr)}
          sub={detail.totals.est_mwh_yr === null ? "Phase 3" : undefined}
        />
        <Kpi label="Mean genset age" value={meanAge === null ? "—" : `${meanAge.toFixed(0)} yr`} />
        <Kpi
          label="Mean efficiency"
          value={meanEff === null ? "—" : `${meanEff.toFixed(1)} %`}
          sub={perf[0]?.efficiency_best_source?.replace(/_/g, " ")}
        />
        <Kpi
          label="Summer run hours"
          value={fmtInt(avg(inBand.map((u) => u.summer_run_hours_avg)))}
          sub="Jun to Sep, CEMS"
        />
      </section>

      <section>
        <h3 className="eyebrow mb-1">Location</h3>
        <dl className="kv">
          <dt>Address</dt>
          <dd>
            {[s.street, s.city, s.state, s.zip].filter(Boolean).join(", ") || "—"}
            {s.county ? ` · ${s.county} County` : ""}
          </dd>
          <dt>Coordinates</dt>
          <dd>
            <span className="num" data-testid="site-coords">
              {fmtCoord(s.lat, s.lon)}
            </span>{" "}
            <Pill tone={s.coord_source === "manual" ? "ok" : "muted"}>
              {s.coord_source}
              {s.coord_precision_m ? ` ±${s.coord_precision_m} m` : ""}
            </Pill>
            {s.coord_verified_at ? (
              <span className="ml-1 text-[11px]" style={{ color: "var(--muted)" }}>
                verified {fmtDate(s.coord_verified_at)}
              </span>
            ) : null}
          </dd>
          <dt>OSM footprint</dt>
          <dd>
            {detail.osm.features.length === 0 ? (
              <Pill>no power=plant polygon within 1.5 km</Pill>
            ) : detail.osm.matched ? (
              <Pill tone="ok">point inside plant footprint</Pill>
            ) : (
              <Pill tone={(detail.osm.nearest_m ?? 0) > 300 ? "danger" : "muted"}>
                nearest footprint {fmtInt(detail.osm.nearest_m)} m away
              </Pill>
            )}
            {detail.osm.features[0] ? (
              <>
                {" "}
                <a
                  href={detail.osm.features[0].url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px]"
                >
                  {detail.osm.features[0].name ??
                    `${detail.osm.features[0].osm_type}/${detail.osm.features[0].osm_id}`}
                </a>
              </>
            ) : null}
          </dd>
        </dl>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {verify ? (
            pinActive ? (
              <>
                <span className="num text-[12px]" data-testid="pin-coords">
                  pin {verify.pin ? fmtCoord(verify.pin.lat, verify.pin.lon) : ""}
                </span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={verify.save}
                  disabled={verify.saving}
                  data-testid="pin-save"
                >
                  {verify.saving ? "Saving…" : "Save position"}
                </button>
                <button type="button" className="btn btn-sm" onClick={verify.cancel}>
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => verify.start(s.id, s.lat, s.lon)}
                data-testid="verify-on-satellite"
              >
                Verify on satellite
              </button>
            )
          ) : (
            <Link
              to="/"
              search={{ site: s.id, c: [s.lon, s.lat], z: 15, sat: true }}
              className="btn btn-sm"
            >
              Open on map
            </Link>
          )}
          <a
            className="btn btn-ghost btn-sm"
            href={googleMapsSatellite(s.lat, s.lon)}
            target="_blank"
            rel="noreferrer"
          >
            Google Maps ↗
          </a>
          <a
            className="btn btn-ghost btn-sm"
            href={googleEarth(s.lat, s.lon)}
            target="_blank"
            rel="noreferrer"
          >
            Google Earth ↗
          </a>
          <a
            className="btn btn-ghost btn-sm"
            href={streetView(s.lat, s.lon)}
            target="_blank"
            rel="noreferrer"
          >
            Street View ↗
          </a>
          <a
            className="btn btn-ghost btn-sm"
            href={openStreetMap(s.lat, s.lon)}
            target="_blank"
            rel="noreferrer"
          >
            OSM ↗
          </a>
        </div>
        <p className="mt-1 text-[11px]" style={{ color: "var(--muted-2)" }}>
          Deep links open in a new tab for eyes-on checks of weather hoods and filter houses.
          Nothing from Google is embedded.
        </p>
      </section>

      <section>
        <h3 className="eyebrow mb-1">Plant</h3>
        <dl className="kv">
          <dt>Operator</dt>
          <dd>{s.operator?.name ?? "—"}</dd>
          {s.owners.filter((o) => o.relationship !== "operator" || o.id !== s.operator?.id).length >
          0 ? (
            <>
              <dt>Owners</dt>
              <dd>
                {s.owners
                  .filter((o) => o.relationship !== "operator" || o.id !== s.operator?.id)
                  .map(
                    (o) =>
                      `${o.name}${o.fraction_owned ? ` (${Math.round(o.fraction_owned * 100)} %)` : ""}`,
                  )
                  .join("; ")}
              </dd>
            </>
          ) : null}
          <dt>Aliases</dt>
          <dd>
            {aliasDraft === null ? (
              <>
                {s.aliases.length ? (
                  s.aliases.join(", ")
                ) : (
                  <span style={{ color: "var(--muted-2)" }}>none</span>
                )}{" "}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setAliasDraft(s.aliases.join(", "))}
                >
                  edit
                </button>
              </>
            ) : (
              <span className="flex gap-1">
                <input
                  className="input flex-1"
                  value={aliasDraft}
                  onChange={(e) => setAliasDraft(e.target.value)}
                  aria-label="Aliases, comma separated"
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={saveAliases}
                  disabled={patch.isPending}
                >
                  Save
                </button>
                <button type="button" className="btn btn-sm" onClick={() => setAliasDraft(null)}>
                  Cancel
                </button>
              </span>
            )}
          </dd>
          <dt>Sector</dt>
          <dd>
            {s.sector_id ? label(SECTOR_LABEL, String(s.sector_id)) : (s.sector_label ?? "—")}
          </dd>
          <dt>Balancing authority</dt>
          <dd>
            {s.balancing_authority ?? "—"}
            {s.iso_rto ? ` · ${s.iso_rto}` : ""}
            {s.nerc_region ? ` · ${s.nerc_region}` : ""}
          </dd>
          <dt>Site type</dt>
          <dd>{label(SITE_TYPE_LABEL, s.site_type)}</dd>
          <dt>Status</dt>
          <dd>
            {s.status}
            {s.first_operating_year ? ` · first unit ${s.first_operating_year}` : ""}
          </dd>
          <dt>Köppen</dt>
          <dd>{s.koppen ?? <span style={{ color: "var(--muted-2)" }}>Phase 3</span>}</dd>
          <dt>AIS type</dt>
          <dd>
            <select
              className="input"
              value={s.ais_type}
              onChange={(e) => saveAis(e.target.value)}
              aria-label="Air intake system type"
              disabled={patch.isPending}
              data-testid="ais-type"
            >
              {AisType.options.map((o) => (
                <option key={o} value={o}>
                  {AIS_LABEL[o] ?? o}
                </option>
              ))}
            </select>
            <span className="ml-2 text-[11px]" style={{ color: "var(--muted-2)" }}>
              editable; writes a reviewer claim
            </span>
          </dd>
          <dt>Identifiers</dt>
          <dd className="num">
            EIA {s.eia_plant_id ?? "—"}
            {s.epa_facility_id ? ` · EPA ${s.epa_facility_id}` : ""}
            {s.gem_plant_id ? ` · GEM ${s.gem_plant_id}` : ""}
          </dd>
        </dl>
      </section>

      <section data-testid="enrichment-summary">
        <h3 className="eyebrow mb-1">Enrichment</h3>
        {detail.claims.length === 0 ? (
          <p style={{ color: "var(--muted)" }}>
            Not researched yet. Turbine model, inlet cooling and contacts read Unknown until the
            agents have run for this site.
          </p>
        ) : (
          <p>
            <span className="num">{detail.claims_summary.accepted}</span> accepted ·{" "}
            <span className="num">{detail.claims_summary.proposed}</span> awaiting review ·{" "}
            <span className="num">{detail.claims_summary.rejected}</span> rejected · last claim{" "}
            {fmtDate(detail.claims.reduce((m, c) => (c.created_at > m ? c.created_at : m), ""))} ·{" "}
            <Link to="/sites/$id" params={{ id: String(s.id) }} search={{ tab: "evidence" }}>
              evidence
            </Link>
            {detail.claims_summary.proposed > 0 ? (
              <>
                {" · "}
                <Link to="/review" search={{ site_id: s.id }}>
                  review this site
                </Link>
              </>
            ) : null}
          </p>
        )}
      </section>

      <section>
        <h3 className="eyebrow mb-1">Nearest BBM reference install</h3>
        {detail.nearest_bbm_reference ? (
          <p>
            <span className="font-medium">{detail.nearest_bbm_reference.project}</span>
            {detail.nearest_bbm_reference.country
              ? `, ${detail.nearest_bbm_reference.country}`
              : ""}{" "}
            · {detail.nearest_bbm_reference.gt_type} · {detail.nearest_bbm_reference.system_type}{" "}
            {detail.nearest_bbm_reference.year} ·{" "}
            <span className="num">{fmtKm(detail.nearest_bbm_reference.distance_km)}</span>
          </p>
        ) : (
          <p style={{ color: "var(--muted)" }}>No geocoded North American reference.</p>
        )}
      </section>

      {detail.lists.length > 0 ? (
        <section>
          <h3 className="eyebrow mb-1">In lists</h3>
          <div className="flex flex-wrap gap-1">
            {detail.lists.map((l) => (
              <Link key={l.id} to="/lists/$id" params={{ id: String(l.id) }} className="chip">
                {l.name}
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div
      className="rounded border px-2 py-1.5"
      style={{ borderColor: "var(--line)", background: "var(--panel-2)" }}
    >
      <div className="eyebrow">{label}</div>
      <div className="num text-[15px]">{value}</div>
      {sub ? (
        <div className="text-[11px]" style={{ color: "var(--muted-2)" }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}
